import { Button } from '@ui/button.tsx';
import { PasswordInput } from '@ui/password-input.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { usePostForgotPasswordToken } from '@r/api/generated/client.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@ui/alert.tsx';
import { PASSWORD_REGEX } from '@r/utils/regex/password.ts';

export const UpdatePasswordForm = ({ tokenValue }: { tokenValue: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  //TODO : use regex from the backend to validate the password
  const updatePasswordSchema = z
    .object({
      password: z.string().regex(PASSWORD_REGEX, {
        message: t('auth.password.invalid')
      }),
      passwordConfirmation: z.string().regex(PASSWORD_REGEX, {
        message: t('auth.password.invalid')
      })
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      path: ['passwordConfirmation'],
      message: t('auth.password.confirmation.invalid')
    });

  const form = useForm<z.infer<typeof updatePasswordSchema>>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: ''
    }
  });

  const updatePasswordMutation = usePostForgotPasswordToken({
    mutation: {
      onSuccess: () => {
        queryClient.setQueryData(['update_password'], { success: true });
      }
    }
  });

  function onSubmit(data: z.infer<typeof updatePasswordSchema>) {
    updatePasswordMutation.mutate({
      tokenValue,
      data: { plainPassword: data.password }
    });
  }

  return (
    <div className="space-y-8">
      {updatePasswordMutation.error ? (
        <Alert variant="destructive" className="mb-10">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Your session has expired.</AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.password.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('auth.password.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.password.repeat.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('auth.password.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className="w-full" type="submit" disabled={updatePasswordMutation.isPending}>
            {updatePasswordMutation.isPending ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Réinitialiser le mot de passe
              </>
            ) : (
              'Réinitialiser le mot de passe'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
